import React, { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProdutosContext } from 'contexts/ProdutosContext';
import { ProdutosServicosContext } from 'contexts/ProdutosServicosContext';
import { DropsContext } from 'contexts/DropsContext';
import { GridContext } from 'contexts/GridContext';
import { AuthContext } from 'contexts/AuthContext';
import { useModal } from 'components/Modals';
import DeleteModal from 'components/Modals/DeleteModal';
import ResumoModal from 'components/Modals/ResumoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import columnOverrides from './columnOverrides';
import getServicosFilterFields from './filterFields';

const Serviços = () => {
  const rotina = 'ProdutoServico';
  const titulo = 'Serviços';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const { user } = useContext(AuthContext);
  const { drops } = useContext(DropsContext);
  const { deleteGrid, exportPdf, exportXls } = useContext(GridContext);
  const { putProdutoStatus, postProdutoCopia } = useContext(ProdutosContext);
  const { produtosServicos, getProdutosServicos, getLoading } = useContext(
    ProdutosServicosContext
  );
  const filterFields = useMemo(() => getServicosFilterFields(drops), [drops]);

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const loadGrid = (props = {}) => {
    const filter = props?.filter || getValues();

    closeModal();

    getProdutosServicos({ ...props, rotina, filter });
  };

  const toolbarActions = [
    {
      name: 'Adicionar',
      icon: 'add',
      placement: 'primary',
      show: !user?.permissoes?.block_inclui_produto,
      action: () => navigate('/app/Servicos/Manutencao'),
    },
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({
          rotina,
          filter: getValues(),
          order: produtosServicos?.order,
        }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({
          rotina,
          filter: getValues(),
          order: produtosServicos?.order,
        }),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={produtosServicos?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getValues() },
              })
            }
          />
        ),
    },
  ];

  const rowActions = [
    {
      name: 'Detalhes',
      icon: 'visibility',
      show: () => !user?.permissoes?.block_detalhe_produto,
      action: ({ row }) => navigate(`/app/Produtos/Detalhes/${row?.id}`),
    },
    {
      name: 'Editar',
      icon: 'edit',
      show: () => !user?.permissoes?.block_altera_produto,
      action: ({ row }) =>
        navigate('/app/Servicos/Manutencao', { state: { id: row?.id } }),
    },
    {
      name: 'Excluir',
      icon: 'delete',
      show: () => !user?.permissoes?.block_exclui_produto,
      action: ({ row }) =>
        openModal(
          <DeleteModal
            onSubmit={() =>
              deleteGrid({
                params: { rotina, id: row?.id },
                cb: () => {
                  loadGrid();
                },
              })
            }
          />
        ),
    },
    {
      name: 'Ativar',
      icon: 'key',
      menu: true,
      show: ({ row }) =>
        row?.ativo !== 'SIM' && !user?.permissoes?.block_ativa_produto,
      action: ({ row }) => putProdutoStatus({ id: row?.id, cb: loadGrid }),
    },
    {
      name: 'Inativar',
      icon: 'key_off',
      menu: true,
      show: ({ row }) =>
        row?.ativo === 'SIM' && !user?.permissoes?.block_inativa_produto,
      action: ({ row }) => putProdutoStatus({ id: row?.id, cb: loadGrid }),
    },
    {
      name: 'Duplicar',
      icon: 'file_copy',
      menu: true,
      show: () => !user?.permissoes?.block_duplica_produto,
      action: ({ row }) => {
        const result = window.confirm(`Deseja realmente duplicar o serviço?`);
        if (result) {
          postProdutoCopia({ data: { id: row?.id }, cb: loadGrid });
        }
      },
    },
  ];

  return (
    <Container fillHeight>
      <Header
        rotina={rotina}
        titulo={titulo}
        colunas={produtosServicos?.colunas}
      />
      <Card fillHeight>
        <DataView
          fillHeight
          grid={produtosServicos}
          filterFields={filterFields}
          filterForm={filterForm}
          onFilter={applyFilter}
          columnOverrides={columnOverrides}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          checkboxSelection
          loading={getLoading}
          loadGrid={loadGrid}
        />
      </Card>
    </Container>
  );
};

export default Serviços;

